import React, { useState, useEffect, useRef } from 'react';
import { 
  Home, Plane, Users, Handshake, Mail, Trophy, FileText,
  Loader2, Save, RotateCcw, Upload, Eye, X, Globe, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../../services/contentApi';
import { useLanguage } from '../../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const pageIcons = {
  home: Home,
  aircraft: Plane,
  team: Users,
  sponsors: Handshake,
  contact: Mail,
  achievements: Trophy,
  buildlogs: FileText
};

const sectionLabels = {
  hero: '🎯 Hero Bölümü',
  mission: '🎯 Misyon Bölümü',
  features: '⚡ Özellikler',
  updates: '📰 Güncellemeler',
  advisor: '👨‍🏫 Danışman',
  members: '👥 Üyeler',
  stats: '📊 İstatistikler',
  partners: '🤝 Partnerler',
  cta: '💼 Sponsor Ol',
  benefits: '✨ Faydalar',
  impact: '📊 Etki',
  form: '📝 Form',
  info: 'ℹ️ Bilgiler',
  social: '🌐 Sosyal Medya',
  faq: '❓ SSS',
  featured: '⭐ Öne Çıkanlar',
  timeline: '📅 Zaman Çizelgesi',
  main: '📄 Ana İçerik'
};

const AdminContent = () => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('en'); // 'en' or 'tr'
  const fileInputRef = useRef(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const data = await contentAPI.getPages();
      console.log('Fetched pages from API:', data);
      
      // Ensure aircraft is included
      const hasAircraft = data.some(p => p.id === 'aircraft');
      if (!hasAircraft) {
        data.splice(1, 0, { id: 'aircraft', name: 'Araçlarımız', icon: 'plane' });
      }
      
      setPages(data);
      if (data.length > 0) {
        selectPage(data[0].id);
      }
    } catch (err) {
      console.error('Error fetching pages:', err);
      toast.error(t('admin.toast.error'));
    } finally {
      setLoading(false);
    }
  };

  const selectPage = async (pageId) => {
    setLoading(true);
    setHasChanges(false);
    try {
      const data = await contentAPI.getPageContent(pageId);
      setSelectedPage(pageId);
      setPageContent(data.content);
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (key, value, lang = 'en') => {
    if (lang === 'tr') {
      // Update Turkish value
      setPageContent(prev => ({
        ...prev,
        [key]: { ...prev[key], value_tr: value }
      }));
    } else {
      // Update English value
      setPageContent(prev => ({
        ...prev,
        [key]: { ...prev[key], value }
      }));
    }
    setHasChanges(true);
  };

  const handleImageUpload = async (e, contentKey) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingKey(contentKey);
    try {
      const result = await contentAPI.uploadImage(file, selectedPage, contentKey);
      // Cloudinary returns full URL, use it directly
      const imageUrl = result.url.startsWith('http') ? result.url : `${API_BASE_URL}${result.url}`;
      handleContentChange(contentKey, imageUrl, 'en');
      toast.success(t('admin.toast.saved'));
    } catch (err) {
      toast.error(err.message || t('admin.toast.error'));
    } finally {
      setUploadingKey(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await contentAPI.updatePageContent(selectedPage, pageContent);
      toast.success(t('admin.toast.saved'));
      setHasChanges(false);
    } catch (err) {
      toast.error(err.message || t('admin.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t('admin.common.confirmDelete'))) return;
    
    try {
      await contentAPI.resetPage(selectedPage);
      await selectPage(selectedPage);
      toast.success(t('admin.toast.updated'));
    } catch (err) {
      toast.error(t('admin.toast.error'));
    }
  };

  const groupContentBySection = () => {
    if (!pageContent) return {};
    
    const grouped = {};
    Object.entries(pageContent).forEach(([key, item]) => {
      const section = item.section || 'main';
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push({ key, ...item });
    });
    return grouped;
  };

  const getCurrentValue = (item) => {
    if (activeLanguage === 'tr') {
      return item.value_tr || '';
    }
    return item.value || '';
  };

  if (loading && !selectedPage) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  const groupedContent = groupContentBySection();

  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>{t('admin.sidebar.content')}</h1>
          <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
            Sayfa içeriklerini buradan düzenleyebilirsiniz
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-xs px-3 py-1 rounded-full" style={{ 
              background: 'rgba(245, 158, 11, 0.2)', 
              color: '#F59E0B' 
            }}>
              Kaydedilmemiş değişiklikler
            </span>
          )}
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              background: previewMode ? 'rgba(0, 240, 255, 0.2)' : 'rgba(148, 163, 184, 0.2)',
              color: previewMode ? '#00F0FF' : '#94A3B8',
              border: previewMode ? '1px solid rgba(0, 240, 255, 0.3)' : '1px solid transparent'
            }}
          >
            <Eye size={18} />
            Önizleme
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}
          >
            <RotateCcw size={18} />
            Sıfırla
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50"
            style={{ background: '#00F0FF', color: '#0F172A' }}
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save size={18} />}
            {t('admin.common.save')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Page Selector */}
        <div className="col-span-3">
          <div className="glass-card p-4">
            <h3 className="text-sm font-medium mb-4" style={{ color: '#94A3B8' }}>Sayfalar</h3>
            <div className="space-y-2">
              {pages.map((page) => {
                const Icon = pageIcons[page.id] || Home;
                return (
                  <button
                    key={page.id}
                    onClick={() => selectPage(page.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                      selectedPage === page.id ? 'active' : ''
                    }`}
                    style={{
                      background: selectedPage === page.id 
                        ? 'rgba(0, 240, 255, 0.15)' 
                        : 'transparent',
                      color: selectedPage === page.id ? '#00F0FF' : '#94A3B8',
                      border: selectedPage === page.id 
                        ? '1px solid rgba(0, 240, 255, 0.3)' 
                        : '1px solid transparent',
                    }}
                  >
                    <Icon size={18} />
                    <span>{page.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Editor */}
        <div className="col-span-9">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Language Toggle */}
              <div className="glass-card p-4">
                <div className="flex items-center gap-2">
                  <Globe size={18} style={{ color: '#94A3B8' }} />
                  <span className="text-sm" style={{ color: '#94A3B8' }}>İçerik Dili:</span>
                  <div className="flex items-center gap-2 p-1 rounded-lg ml-2" style={{ background: 'rgba(15, 23, 42, 0.6)' }}>
                    <button
                      onClick={() => setActiveLanguage('en')}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                      style={{
                        background: activeLanguage === 'en' ? 'rgba(0, 240, 255, 0.2)' : 'transparent',
                        color: activeLanguage === 'en' ? '#00F0FF' : '#94A3B8',
                        border: activeLanguage === 'en' ? '1px solid #00F0FF' : '1px solid transparent'
                      }}
                    >
                      {t('admin.common.english')}
                    </button>
                    <button
                      onClick={() => setActiveLanguage('tr')}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                      style={{
                        background: activeLanguage === 'tr' ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                        color: activeLanguage === 'tr' ? '#EF4444' : '#94A3B8',
                        border: activeLanguage === 'tr' ? '1px solid #EF4444' : '1px solid transparent'
                      }}
                    >
                      {t('admin.common.turkish')}
                    </button>
                  </div>
                </div>
                {activeLanguage === 'tr' && (
                  <p className="text-xs mt-3 p-3 rounded-lg" style={{ 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#F8FAFC' 
                  }}>
                    💡 Türkçe içerik girdiğinizde, site Türkçe modda bu içerikleri gösterecektir. Boş bırakırsanız İngilizce içerik gösterilir.
                  </p>
                )}
              </div>

              {Object.entries(groupedContent).map(([section, items]) => (
                <div key={section} className="glass-card p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#F8FAFC' }}>
                    {sectionLabels[section] || section}
                  </h3>
                  
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div key={item.key} className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium" style={{ color: '#94A3B8' }}>
                          {item.label}
                          {activeLanguage === 'tr' && (
                            <span className="text-xs px-2 py-0.5 rounded" style={{ 
                              background: 'rgba(239, 68, 68, 0.2)', 
                              color: '#EF4444' 
                            }}>TR</span>
                          )}
                          {activeLanguage === 'en' && (
                            <span className="text-xs px-2 py-0.5 rounded" style={{ 
                              background: 'rgba(0, 240, 255, 0.2)', 
                              color: '#00F0FF' 
                            }}>EN</span>
                          )}
                        </label>
                        
                        {item.type === 'image' ? (
                          <div className="flex items-start gap-4">
                            {/* Image Preview */}
                            <div 
                              className="w-40 h-24 rounded-lg bg-cover bg-center flex-shrink-0 relative"
                              style={{ 
                                backgroundImage: `url(${item.value})`,
                                background: item.value ? `url(${item.value}) center/cover` : 'rgba(15, 23, 42, 0.8)',
                                border: '1px solid rgba(0, 240, 255, 0.2)'
                              }}
                            >
                              {!item.value && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <ImageIcon size={24} style={{ color: '#64748B' }} />
                                </div>
                              )}
                            </div>
                            
                            {/* Upload Controls */}
                            <div className="flex-1 space-y-2">
                              <input
                                type="url"
                                value={item.value}
                                onChange={(e) => handleContentChange(item.key, e.target.value, 'en')}
                                className="w-full px-3 py-2 text-sm rounded-lg"
                                style={{
                                  background: 'rgba(15, 23, 42, 0.8)',
                                  border: '1px solid rgba(0, 240, 255, 0.3)',
                                  color: '#F8FAFC'
                                }}
                                placeholder="Görsel URL'si veya dosya yükleyin"
                              />
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer text-sm"
                                  style={{ background: 'rgba(0, 240, 255, 0.1)', color: '#00F0FF' }}
                                >
                                  {uploadingKey === item.key ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Upload size={16} />
                                  )}
                                  <span>Dosya Yükle</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleImageUpload(e, item.key)}
                                    disabled={uploadingKey === item.key}
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                        ) : (getCurrentValue(item) && getCurrentValue(item).includes('\n')) || (item.value && item.value.includes('\n')) ? (
                          <textarea
                            value={getCurrentValue(item)}
                            onChange={(e) => handleContentChange(item.key, e.target.value, activeLanguage)}
                            rows={4}
                            className="w-full px-4 py-3 resize-none rounded-lg"
                            style={{
                              background: 'rgba(15, 23, 42, 0.8)',
                              border: activeLanguage === 'tr' 
                                ? '1px solid rgba(239, 68, 68, 0.3)' 
                                : '1px solid rgba(0, 240, 255, 0.3)',
                              color: '#F8FAFC'
                            }}
                            placeholder={activeLanguage === 'tr' ? `Türkçe: ${item.value || ''}` : ''}
                          />
                        ) : (
                          <input
                            type="text"
                            value={getCurrentValue(item)}
                            onChange={(e) => handleContentChange(item.key, e.target.value, activeLanguage)}
                            className="w-full px-4 py-3 rounded-lg"
                            style={{
                              background: 'rgba(15, 23, 42, 0.8)',
                              border: activeLanguage === 'tr' 
                                ? '1px solid rgba(239, 68, 68, 0.3)' 
                                : '1px solid rgba(0, 240, 255, 0.3)',
                              color: '#F8FAFC'
                            }}
                            placeholder={activeLanguage === 'tr' ? `Türkçe: ${item.value || ''}` : ''}
                          />
                        )}
                        
                        {/* Show English value as reference when editing Turkish */}
                        {activeLanguage === 'tr' && item.value && item.type !== 'image' && (
                          <p className="text-xs" style={{ color: '#64748B' }}>
                            İngilizce: {item.value.substring(0, 100)}{item.value.length > 100 ? '...' : ''}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
      />

      {/* Preview Modal */}
      {previewMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
          <div className="relative w-full max-w-6xl h-[80vh] rounded-lg overflow-hidden" style={{ background: '#0F172A' }}>
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={() => setPreviewMode(false)}
                className="p-2 rounded-full"
                style={{ background: 'rgba(0,0,0,0.5)', color: '#F8FAFC' }}
              >
                <X size={24} />
              </button>
            </div>
            <iframe
              src={`/${selectedPage === 'home' ? '' : selectedPage}`}
              className="w-full h-full"
              title="Preview"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContent;
